import React, { useEffect, useState } from 'react';
import { Modal, Alert, Spin, Button, Space, Typography } from 'antd';
import { CloudDownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

const { Text, Paragraph } = Typography;

const AudioVideoModal = ({ visible, onClose, mediaType }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileExists, setFileExists] = useState(null);
  
  // Access the media URLs from Redux state
  const { mediaUrls, loading: mediaStateLoading } = useSelector(state => state.mediaState);
  
  // Get the appropriate URL based on the selected media type
  const mediaUrl = mediaType === 'audio' ? mediaUrls?.audio : mediaUrls?.video;

  // Check if the media URL is valid
  useEffect(() => {
    if (visible) {
      setLoading(true);
      setError(null);
      

      if (mediaUrl) {
        // For cloud storage URLs, we'll just assume the file exists
        // since these are pre-signed URLs that were just generated
        setFileExists(true);
        
        // Add console logging for debugging
        //console.log(`Loading ${mediaType} with URL:`, mediaUrl);
      } else {
        setError(`No ${mediaType} URL available for this assessment`);
        setLoading(false);
      }

    }
  }, [visible, mediaUrl, mediaType]);

  const handleLoadedData = () => {
    setLoading(false);
  };

  const handleError = (e) => {
    console.error("Media error:", e);
    setLoading(false);
    setError(`Failed to load ${mediaType}. This could be due to CORS restrictions or the file might be in an unsupported format.`);
  };

  const getDirectMediaUrl = () => {
    // For development testing - open the direct URL in a new tab
    if (mediaUrl) {
      window.open(mediaUrl, '_blank');
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button 
          key="directUrl" 
          type="primary" 
          onClick={getDirectMediaUrl}
          disabled={!mediaUrl}
          icon={<CloudDownloadOutlined />}
        >
          Open Direct URL
        </Button>
      ]}
      destroyOnClose
      title={`${mediaType === 'audio' ? "Audio" : "Video"} Recording`}
      width={mediaType === 'audio' ? 500 : 800}
      centered
    >
      {loading && fileExists !== false && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin tip={`Loading ${mediaType}...`} />
          <Paragraph style={{ marginTop: 15 }}>
            If loading takes too long, try using the "Open Direct URL" button below.
          </Paragraph>
        </div>
      )}
      
      {error && (
        <Alert
          message="Media Error"
          description={
            <div>
              <p>{error}</p>
              <p>
                <ExclamationCircleOutlined style={{ marginRight: 5 }} />
                <Text strong>Troubleshooting:</Text>
              </p>
              <ul>
                <li>Ensure the file exists in the <Text code>media_uploads</Text> directory on the server</li>
                <li>Check that the backend server is running and properly configured for CORS</li>
                <li>Try clicking "Open Direct URL" below to see if direct access works</li>
              </ul>
              <Text type="secondary">Media URL: {mediaUrl}</Text>
            </div>
          }
          type="error"
          showIcon
        />
      )}
      
      {mediaUrl && fileExists !== false ? (
        mediaType === 'audio' ? (
          <audio 
            controls 
            autoPlay 
            style={{ width: '100%', display: loading ? 'none' : 'block' }}
            onLoadedData={handleLoadedData}
            onError={handleError}
            crossOrigin="anonymous"
          >
            <source src={mediaUrl} type="audio/webm" />
            <source src={mediaUrl} type="audio/wav" />
            <source src={mediaUrl} type="audio/mp3" />
            Your browser does not support the audio element.
          </audio>
        ) : (
          <video 
            controls 
            autoPlay 
            style={{ width: '100%', display: loading ? 'none' : 'block' }}
            onLoadedData={handleLoadedData}
            onError={handleError}
            crossOrigin="anonymous"
          >
            <source src={mediaUrl} type="video/webm" />
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )
      ) : null}
    </Modal>
  );
};

export default AudioVideoModal;
